const DEFAULT_TEST_CASE_COUNT = 5
const MIN_TEST_CASE_COUNT = 1
// The draft action carries `requested_total` (1-500) and stages the request in batches, so the
// prompt asks for the whole number the user wants; the batch contract bounds each batch.
const MAX_TEST_CASE_COUNT = 500

const TEST_CASE_COUNT_WORDS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
  twenty: 20,
}

const COUNT_WORD_ALTERNATION = Object.keys(TEST_CASE_COUNT_WORDS).join("|")

// Not preceded by a digit or separator, so ids and dates ("2026-01-05") never
// read as a count.
const COUNT_TOKEN = `(?<![\\d\\-/.])\\b(\\d{1,3}|${COUNT_WORD_ALTERNATION})\\b`

const CASE_NOUNS = "(?:(?:test|use|edge)[ -]?)?(?:cases?|scenarios?|examples?)"

const GENERATION_VERBS = "draft|create|generate|write|make|add|produce|build"

// A generation verb is only a request where the user could be addressing us:
// opening the goal or a sentence, after an adverb of request, after "you", or
// after "need/want/like to". Everywhere else the verb describes the user's own
// system ("our workflow can generate 8 scenarios per run", "the agent should
// produce 3 examples"), and that count is not what they asked us to draft.
const REQUEST_POSITION =
  "(?:^|[.!?;:\\n]\\s*|\\byou\\s+|\\b(?:need|want|like|love)\\s+(?:me\\s+|us\\s+|you\\s+)?to\\s+)" +
  "(?:please\\s+|just\\s+|only\\s+|also\\s+|now\\s+|then\\s+|and\\s+)*"

// "create 3 test cases", "draft 24 cases", "generate 8 scenarios". Scoped to a
// generation verb in a request position so inventory the user is describing
// ("we already have 100 test cases") never outranks what they are asking for.
const VERB_SCOPED_COUNT_PATTERN = new RegExp(
  `${REQUEST_POSITION}(?:${GENERATION_VERBS})\\s+(?:me\\s+|us\\s+)?${COUNT_TOKEN}\\s+${CASE_NOUNS}\\b`,
  "i",
)

// "30 test cases for onboarding" — a count with no generation verb anywhere.
// Kept narrow to an explicit test/use case noun, and never the inventory the
// user says they already hold ("we already have 100 test cases").
const INVENTORY_LEAD_IN =
  "(?<!\\b(?:have|has|had|contain|contains|containing|include|includes|including|with|already)\\s)"
const BARE_CASE_COUNT_PATTERN = new RegExp(
  `${INVENTORY_LEAD_IN}${COUNT_TOKEN}\\s+(?:test|use|edge)[ -]?cases?\\b`,
  "i",
)

// "... draft 3." with no noun. Restricted to a verb opening a sentence, so an
// asserted requirement ("it must generate 3.") is not read as a request.
const IMPERATIVE_COUNT_PATTERN = new RegExp(
  `(?:^|[.!?;]\\s+)(?:please\\s+)?(?:${GENERATION_VERBS})\\s+(?:me\\s+)?${COUNT_TOKEN}\\s*(?:[.,;:!?)\\]]|$)`,
  "i",
)

// Markdown emphasis around the number ("Create **24** test cases") would otherwise break the
// number/noun adjacency. Struck-through text is dropped rather than unwrapped: "~~24~~ ... actually
// 3" is a correction, so its count must not outrank the one that replaced it. Underscores and code
// spans are left alone so an identifier like `refund_24_cases` stays one token.
function stripEmphasis(goal: string): string {
  return goal.replace(/~~[\s\S]*?~~/g, " ").replace(/\*+/g, " ")
}

function getRequestedTestCaseCount(goal: string): number {
  const normalizedGoal = stripEmphasis(goal)
  const countToken = (normalizedGoal.match(VERB_SCOPED_COUNT_PATTERN) ??
    normalizedGoal.match(IMPERATIVE_COUNT_PATTERN) ??
    normalizedGoal.match(BARE_CASE_COUNT_PATTERN))?.[1]?.toLowerCase()

  if (!countToken) {
    return DEFAULT_TEST_CASE_COUNT
  }

  const count = TEST_CASE_COUNT_WORDS[countToken] ?? Number(countToken)
  if (!Number.isFinite(count) || count < MIN_TEST_CASE_COUNT) {
    return DEFAULT_TEST_CASE_COUNT
  }

  // Clamp rather than fall back: an absurd number becomes the ceiling the draft action accepts,
  // not the default, and never the per-batch size — Sofia stages the total in batches.
  return Math.min(count, MAX_TEST_CASE_COUNT)
}

export function buildCreateTestSetPrompt(
  goal: string,
  referenceContext = "",
): string {
  const testCaseCount = getRequestedTestCaseCount(goal)

  return `Draft ${testCaseCount} diverse test cases in the open test set editor for this goal: ${goal}. Include a clear description, realistic simulation input, and useful expected behaviors for each case. Stage them together for review with word-level diffs; do not create API records yet.${referenceContext}`
}
